/* ============================================================
   SAM-1237 — saving the location of an existing business, in the order crud (#884) accepts:

   - same estate group: PATCH business {building_id, estate_id};
   - another group (or from not set / external / virtual): clear building and unit, move or create the primary
     link, then set building and unit;
   - to external / virtual: delete the primary link first, then PATCH business with the new columns.

   The calls go through a small client so the sequence is testable.
   ============================================================ */
import { defaultInstance } from '../../../api/httpClient';
import { locationColumns, sameLocation, type LocationForm } from './locationModel';

export interface BusinessLink { id: number; estate_group_id: number; is_primary: boolean }

export interface LocationClient {
  patchBusiness: (id: string, body: Record<string, unknown>) => Promise<unknown>;
  listLinks: (businessId: string) => Promise<BusinessLink[]>;
  postLink: (body: { business_id: string; estate_group_id: number; is_primary: true }) => Promise<unknown>;
  patchLink: (id: number, body: Record<string, unknown>) => Promise<unknown>;
  deleteLink: (id: number) => Promise<unknown>;
}

const rows = (body: unknown): unknown[] => {
  const data = (body as { data?: unknown } | null)?.data ?? body;
  return Array.isArray(data) ? data : [];
};

export const crudLocationClient: LocationClient = {
  patchBusiness: (id, body) => defaultInstance({ url: `/business/${id}`, method: 'PATCH', data: body }),
  listLinks: async (businessId) => {
    const res = await defaultInstance({ url: `/business_estate_group?business_id=${encodeURIComponent(businessId)}&limit=200`, method: 'GET' });
    return rows(res).map((r) => {
      const row = r as Record<string, unknown>;
      return { id: Number(row.id), estate_group_id: Number(row.estate_group_id), is_primary: row.is_primary === true };
    });
  },
  postLink: (body) => defaultInstance({ url: '/business_estate_group', method: 'POST', data: body }),
  patchLink: (id, body) => defaultInstance({ url: `/business_estate_group/${id}`, method: 'PATCH', data: body }),
  deleteLink: (id) => defaultInstance({ url: `/business_estate_group/${id}`, method: 'DELETE' }),
};

/** Makes `groupId` the primary estate group of the business: moves the primary link, or promotes one that exists. */
export async function setPrimaryGroup(client: LocationClient, businessId: string, groupId: number): Promise<void> {
  const links = await client.listLinks(businessId);
  const current = links.find((l) => l.is_primary);
  const target = links.find((l) => l.estate_group_id === groupId);
  if (target?.is_primary) return;
  if (current && !target) { await client.patchLink(current.id, { estate_group_id: groupId }); return; }
  if (current && target) {
    // the old group stops being the location; the link that already exists for the new one takes over
    await client.deleteLink(current.id);
    await client.patchLink(target.id, { is_primary: true });
    return;
  }
  if (target) { await client.patchLink(target.id, { is_primary: true }); return; }
  await client.postLink({ business_id: businessId, estate_group_id: groupId, is_primary: true });
}

export async function saveLocation(client: LocationClient, businessId: string, before: LocationForm, after: LocationForm): Promise<void> {
  if (sameLocation(before, after) || after.type === '') return;

  if (after.type === 'internal') {
    const sameGroup = before.type === 'internal' && before.groupId === after.groupId;
    if (sameGroup) {
      await client.patchBusiness(businessId, { building_id: after.buildingId, estate_id: after.unitId });
      return;
    }
    // The stored building and unit belong to the old group: clear them, move the link, then set the new ones.
    await client.patchBusiness(businessId, {
      location_type: 'internal', building_id: null, estate_id: null, address: null, city: null, zip_code: null, country: null,
    });
    await setPrimaryGroup(client, businessId, after.groupId as number);
    if (after.buildingId != null || after.unitId != null) {
      await client.patchBusiness(businessId, { building_id: after.buildingId, estate_id: after.unitId });
    }
    return;
  }

  // external or virtual: no estate group any more
  const links = await client.listLinks(businessId);
  const primary = links.find((l) => l.is_primary);
  if (primary) await client.deleteLink(primary.id);
  await client.patchBusiness(businessId, locationColumns(after));
}

/** After POST /business of an internal location: the primary link, which needs the business to exist. */
export async function linkNewBusiness(client: LocationClient, businessId: string, form: LocationForm): Promise<void> {
  if (form.type === 'internal' && form.groupId != null) {
    await client.postLink({ business_id: businessId, estate_group_id: form.groupId, is_primary: true });
  }
}

/** The text for the refusals crud answers (code in the body) so the admin says why. */
export function locationErrorText(error: unknown): string {
  const data = (error as { response?: { data?: { code?: string; error?: string } } } | null)?.response?.data;
  switch (data?.code) {
    case 'location_outside_estate_group': return 'That building or unit is not in the estate group.';
    case 'location_has_estate_group': return 'Remove the estate group first to make it external or virtual.';
    case 'location_not_internal': return 'Only a business in Cap Cana has an estate group.';
    case 'primary_estate_group_exists': return 'This business already has an estate group.';
    case 'invalid_location': return data?.error || 'The location is not valid.';
    default: return data?.error || 'The location could not be saved.';
  }
}
